<script>
// Cancellation stepper: before a paying user cancels, walk them through
// (1) a cheaper-plan offer, (2) a 3-months-free offer, then (3) the churn
// feedback survey + confirm. Steps 1-2 are skipped when no offer applies.
// The confirm cancels at the end of the billing period (not immediately).
import Multiselect from 'vue-multiselect'
import { getPromoEntry, updatePromoEntry } from '../global/promo/promoState'
import { formatDate } from '../../utils/helpers.js'

// Must match the Name of the Stripe coupon (100% off, repeats for 3 months).
const RETENTION_COUPON = 'retention-3-months-free'
const PROMO_ID = 'retention-3-months-free'

export default {
  name: 'CancelFlowModal',
  components: { Multiselect },
  props: {
    // Public name of the plan being canceled, e.g. "Standard"
    currentPlanName: { type: String, default: '' },
    // ISO date the paid period (and access) ends
    periodEnd: { type: String, default: '' },
    // Cheaper plan to offer, or null to skip step 1:
    // { sku, name, price, unit, note }
    downgradeOffer: { type: Object, default: null },
    // Plan offered free for 3 months, or null to skip step 2: { sku, name }
    freeOffer: { type: Object, default: null },
  },
  data() {
    return {
      name: 'cancelFlowModal',
      stepIndex: 0,
      freeOfferAvailable: false,
      busy: false,
      declinedOffers: [],
      cancelSubscriptionOptions: [
        'Missing Features',
        'Business Downturn',
        'Pricing',
        'Bugs or Issues',
        'Other',
      ],
      cancelSubscriptionOptionSelected: [],
      cancelSubscriptionOptionEdited: false,
      cancelSubscriptionOptionValid: true,
      feedbackValid: true,
      featureList: [
        'Word Document Proofing',
        'Adobe Plugins',
        'Real-time Collaboration',
        'Manage Files in Proof Version',
        'More Zapier Zaps',
        'Art Intake Forms',
        'Reviewer Logins',
        'SSO',
        'Additional Reporting',
        'Additional Translations',
        'Additional Integrations'
      ],
      selectedFeatures: [],
      feedback: "",
      reasonLabel: "What made you decide to cancel your subscription today?",
      featuresLabel: "Is there a specific feature you’d like to see?",
      feedbackLabel: "Do you have anything else you'd like us to know?",
      optionsPlaceholder: "Select one or many",
    }
  },
  computed: {
    steps() {
      const steps = []
      if (this.downgradeOffer) steps.push('downgrade')
      if (this.freeOffer && this.freeOfferAvailable) steps.push('free')
      steps.push('feedback')
      return steps
    },
    currentStep() {
      return this.steps[this.stepIndex]
    },
    freeOfferIsDowngrade() {
      return !!this.freeOffer && !!this.currentPlanName && this.freeOffer.name !== this.currentPlanName;
    },
    periodEndDisplay() {
      return formatDate(this.periodEnd);
    },
  },
  watch: {
    cancelSubscriptionOptionSelected() {
      this.cancelSubscriptionOptionValid = this.cancelSubscriptionOptionSelected.length > 0;
      this.checkFeedback();
    },
    feedback() {
      this.checkFeedback();
    }
  },
  methods: {
    async open() {
      this.stepIndex = 0;
      this.busy = false;
      this.declinedOffers = [];
      this.cancelSubscriptionOptionSelected = [];
      this.selectedFeatures = [];
      this.feedback = "";
      this.cancelSubscriptionOptionValid = true;
      this.feedbackValid = true;
      this.cancelSubscriptionOptionEdited = false;

      // Free-months offer is once per user; hide it if already redeemed
      // (or if promo state could not be loaded).
      const entry = await getPromoEntry(PROMO_ID);
      this.freeOfferAvailable = !!entry && !entry.converted;

      this.$bvModal.show(this.name);
    },
    declineStep() {
      const offer = this.currentStep === 'downgrade'
        ? `Downgrade to ${this.downgradeOffer.name}`
        : `3 months of ${this.freeOffer.name} free`;
      this.declinedOffers.push(offer);
      this.reportOfferEvent(`Declined: ${offer}`);
      this.stepIndex++;
    },
    async acceptDowngrade() {
      if (this.busy) return;
      this.busy = true;
      const result = await this.$A.BillingService.UpdateSubscription(this.downgradeOffer.sku);
      this.busy = false;
      // null = network/server failure (an error toast was already shown)
      if (!result || (result.success !== undefined && !result.success)) return;
      this.reportOfferEvent(`Accepted: Downgrade to ${this.downgradeOffer.name}`);
      this.$bvModal.hide(this.name);
      this.$emit('offer-accepted', `Switched to ${this.downgradeOffer.name}`);
    },
    async acceptFreeMonths() {
      if (this.busy) return;
      this.busy = true;
      const result = await this.$A.BillingService.UpdateSubscription(this.freeOffer.sku, RETENTION_COUPON);
      this.busy = false;
      // null = network/server failure; the offer must stay claimable on failure
      if (!result || (result.success !== undefined && !result.success)) return;
      this.reportOfferEvent(`Accepted: 3 months of ${this.freeOffer.name} free`);
      await updatePromoEntry(PROMO_ID, { converted: true });
      this.$bvModal.hide(this.name);
      this.$emit('offer-accepted', `Claimed 3 months of ${this.freeOffer.name} free`);
    },
    // Fire-and-forget Slack notice for retention-offer decisions. The Slack
    // endpoint stamps the account id from the caller's identity.
    reportOfferEvent(event) {
      const user = (this.$A.Auth && this.$A.Auth.email) || 'unknown user';
      this.$A.FeedbackService.CreateSlackMessage({
        slackChannel: 'ns_purchase_events',
        subject: 'Retention Offer',
        text: `${event}\nCurrent Plan: ${this.currentPlanName}\nUser: ${user}`,
      });
    },
    confirmCancel() {
      this.cancelSubscriptionOptionEdited = true;

      if (!this.cancelSubscriptionOptionValid || !this.feedbackValid) {
        return;
      }

      this.$emit('confirmed', {
        reason: this.cancelSubscriptionOptionSelected,
        features: this.selectedFeatures,
        feedback: this.feedback,
        offersDeclined: this.declinedOffers,
      });
      this.$bvModal.hide(this.name);
    },
    keepPlan() {
      this.$bvModal.hide(this.name);
    },
    setCancelSubscriptionOptionEdited() {
      this.cancelSubscriptionOptionEdited = true
    },
    checkFeedback() {
      let hasOther = this.cancelSubscriptionOptionSelected.length === 1 && this.cancelSubscriptionOptionSelected[0] === "Other";
      this.feedbackValid = hasOther ? this.feedback !== "" : true;
    },
  },
};
</script>

<template>
  <b-modal :id="name" size="md" centered hide-header hide-footer content-class="cancel-flow-content" body-class="cancel-flow-body">
    <div class="cancel-flow" role="document" aria-label="Cancel subscription">
      <div class="flow-top">
        <button type="button" class="flow-close" aria-label="Close" title="Close" @click="keepPlan">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <!-- Step: cheaper plan -->
      <div v-if="currentStep === 'downgrade'" key="downgrade">
        <div class="flow-eyebrow">Before you go</div>
        <h4 class="flow-title">Would a smaller plan fit better?</h4>
        <p class="flow-copy">
          Keep your proofs, comments, and team — {{ downgradeOffer.name }} has everything you need to keep approvals moving, at a lower price.
        </p>
        <div class="offer-panel">
          <div class="offer-plan-name">{{ downgradeOffer.name }}</div>
          <div class="offer-price-row">
            <span class="offer-price">{{ downgradeOffer.price }}</span>
            <span class="offer-unit">{{ downgradeOffer.unit }}</span>
          </div>
          <div v-if="downgradeOffer.note" class="offer-note">{{ downgradeOffer.note }}</div>
        </div>
        <div class="flow-actions">
          <button type="button" class="flow-btn-ghost" @click="declineStep">No thanks, continue</button>
          <button type="button" class="flow-btn-primary" @click="acceptDowngrade">Switch to {{ downgradeOffer.name }}</button>
        </div>
      </div>

      <!-- Step: 3 months free -->
      <div v-else-if="currentStep === 'free'" key="free">
        <div class="flow-eyebrow">On us</div>
        <h4 class="flow-title" v-if="freeOfferIsDowngrade">Try {{ freeOffer.name }} free for 3 months</h4>
        <h4 class="flow-title" v-else>3 months of {{ freeOffer.name }}, free</h4>
        <p class="flow-copy" v-if="freeOfferIsDowngrade">
          Switch from {{ currentPlanName }} to {{ freeOffer.name }} and try it free for 3 months. Your plan changes to {{ freeOffer.name }} right away, and you can still cancel anytime.
        </p>
        <p class="flow-copy" v-else>
          Stay on {{ freeOffer.name }} and pay nothing for the next 3 months. The discount applies right away, and you can still cancel anytime.
        </p>
        <div class="offer-panel offer-panel--free">
          <div class="offer-plan-name">{{ freeOffer.name }}</div>
          <div class="offer-price-row">
            <span class="offer-price">$0</span>
            <span class="offer-unit">for 3 months</span>
          </div>
          <div class="offer-note">{{ freeOffer.name }}'s regular price resumes after 3 months</div>
        </div>
        <div class="flow-actions">
          <button type="button" class="flow-btn-ghost" @click="declineStep">No thanks, continue</button>
          <button type="button" class="flow-btn-primary" @click="acceptFreeMonths">{{ freeOfferIsDowngrade ? 'Switch to ' + freeOffer.name + ' free' : 'Claim 3 free months' }}</button>
        </div>
      </div>

      <!-- Step: feedback + confirm -->
      <div v-else key="feedback">
        <div class="flow-eyebrow flow-eyebrow--muted">Cancel subscription</div>
        <h4 class="flow-title">Sorry to see you go</h4>
        <p v-if="periodEndDisplay" class="flow-copy">
          Your {{ currentPlanName }} plan stays active until <strong>{{ periodEndDisplay }}</strong> — you won't be billed again.
        </p>

        <div class="flow-field">
          <div class="flow-field-label">
            {{ reasonLabel }}
            <b-badge variant="danger" v-show="cancelSubscriptionOptionEdited && !cancelSubscriptionOptionValid">
              Required
            </b-badge>
          </div>
          <multiselect
              v-model="cancelSubscriptionOptionSelected"
              :options="cancelSubscriptionOptions"
              :placeholder="optionsPlaceholder"
              :multiple="true"
              :show-labels="false"
              :hide-selected="true"
              :clear-on-select="true"
              :searchable="true"
              @close="setCancelSubscriptionOptionEdited"
              :class="cancelSubscriptionOptionEdited && !cancelSubscriptionOptionValid ? 'red-border' : 'green-border'"
          />
        </div>

        <div class="flow-field">
          <div class="flow-field-label">{{ featuresLabel }}</div>
          <multiselect
              v-model="selectedFeatures"
              :options="featureList"
              :placeholder="optionsPlaceholder"
              :multiple="true"
              :show-labels="false"
              :allow-empty="true"
              :hide-selected="true"
              :searchable="true"
              class="green-border"
          />
        </div>

        <div class="flow-field">
          <div class="flow-field-label">
            {{ feedbackLabel }}
            <b-badge variant="danger" v-show="cancelSubscriptionOptionEdited && !feedbackValid">Required</b-badge>
          </div>
          <b-form-textarea
              id="cancel-flow-feedback"
              v-model="feedback"
              placeholder="Your message..."
              rows="3"
              max-rows="6"
              :class="cancelSubscriptionOptionEdited && !feedbackValid ? 'feedback-text-red' : 'feedback-text-green'"
              :autofocus="false"
          ></b-form-textarea>
        </div>

        <div class="flow-actions">
          <button type="button" class="flow-btn-ghost" @click="keepPlan">Keep my plan</button>
          <button type="button" class="flow-btn-danger" @click="confirmCancel">Cancel subscription</button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.cancel-flow {
  position: relative;
}
::v-deep .cancel-flow-content {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 12px 32px -12px rgba(8, 11, 13, 0.25);
}
::v-deep .cancel-flow-body {
  padding: 20px 24px 24px;
}

/* Top row: close button */
.flow-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 24px;
  margin-bottom: 14px;
}
.flow-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: auto;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #9ca3af;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.flow-close:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #111827;
}

/* Copy */
.flow-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #017a48;
  margin-bottom: 6px;
}
.flow-eyebrow--muted {
  color: #6b7280;
}
.flow-title {
  font-size: 19px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}
.flow-copy {
  font-size: 13px;
  line-height: 1.55;
  color: #4b5563;
  margin: 0 0 16px;
}

/* Offer panel */
.offer-panel {
  padding: 16px 18px;
  margin-bottom: 18px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
}
.offer-panel--free {
  background: linear-gradient(180deg, rgba(1, 146, 86, 0.035), rgba(1, 146, 86, 0)) #fff;
  border-color: rgba(1, 146, 86, 0.25);
}
.offer-plan-name {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.offer-price-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 4px;
}
.offer-price {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.1;
  color: #111827;
}
.offer-panel--free .offer-price {
  color: #017a48;
}
.offer-unit {
  font-size: 13px;
  color: #6b7280;
}
.offer-note {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

/* Survey fields */
.flow-field {
  margin-bottom: 14px;
}
.flow-field-label {
  margin-bottom: 5px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

/* Actions */
.flow-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}
.flow-btn-primary,
.flow-btn-ghost,
.flow-btn-danger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 16px;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}
.flow-btn-primary {
  background: #019256;
  border: 1px solid #019256;
  color: #fff;
}
.flow-btn-primary:hover {
  background: #017a48;
  border-color: #017a48;
}
.flow-btn-ghost {
  background: transparent;
  border: 0;
  font-weight: 500;
  color: #6b7280;
}
.flow-btn-ghost:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #111827;
}
.flow-btn-danger {
  background: #D44D68;
  border: 1px solid #D44D68;
  color: #fff;
}
.flow-btn-danger:hover {
  opacity: 0.9;
}

/* Multiselect + textarea validation states (match existing cancel modal) */
.multiselect__option, .multiselect__input {
  font-size: 14px;
}
.feedback-text-green {
  border: 1px solid #019255;
  font-weight: lighter;
  color: #212529;
}
.feedback-text-red {
  border: 1px solid #dc3545;
  font-weight: lighter;
  color: #212529;
}
textarea.form-control {
  padding: 10px;
}
.red-border ::v-deep .multiselect__tags {
  border: 1px #dc3545 solid !important;
}
.green-border ::v-deep .multiselect__tags {
  border: 1px solid #019255 !important;
}

@media (max-width: 575px) {
  ::v-deep .cancel-flow-body {
    padding: 16px 16px 20px;
  }
  .flow-actions {
    flex-wrap: wrap;
  }
}
</style>
